import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  InfoOutlined,
  Close
} from '@mui/icons-material';
import isRecentAlert from '../functions/isRecentAlert';
import { urgencyValue, urgencyBand, urgencyColor, urgencyAccent, urgencyPillLabel } from '../utils/urgency'; // DAS-2532
import { useSelector } from 'react-redux';
import { recentActionText } from '../functions/recentOwnerAction'; // DAS-2951
import valueTrapBadge from '../functions/valueTrapBadge'; // DAS-3119
import earningsBadge from '../functions/earningsBadge'; // DAS-3194
import latestAlertStory from '../functions/latestAlertStory'; // DAS-3129
import api from '../functions/api'; // DAS-3129
import { tickerAlphaChip } from '../functions/tickerAlphaChip'; // DAS-3289 / DAS-3317: one short label

const CompactAlertCard = ({
  alert,
  alertEffectiveness,
  onClose,
  onCheckboxChange,
  onFollowedUpChange,
  onReinvestmentCheck,
  onTickerClick,
  onOpenMarketAlerts,
  openFPDialog
}) => {
  const isHarvest = (alert?.alert_type || '').toLowerCase() === 'harvest';
  const trapBadge = valueTrapBadge(alert); // DAS-3119
  const reportBadge = earningsBadge(alert); // DAS-3194
  const alphaChip = tickerAlphaChip(alert); // DAS-3317
  // DAS-2895: a Harvest alert whose sector/region sits on the losing side of a
  // RISING watchlist risk (DAS-2831 stores the match; +1 = the risk SUPPORTS
  // this sell). Shown as a badge so it is visible without opening the card.
  const riskBacked = isHarvest
    && (alert?.market_fit_source === 'risk_sector' || alert?.market_fit_source === 'risk_region')
    && Number(alert?.market_fit_alignment) > 0
    ? alert?.market_fit_name
    : null;
  
  // DAS-2951: does this alert contradict a trade the owner just made?
  const transactions = useSelector(state => state?.portfolio?.data?.transactions);
  const recentAction = recentActionText(alert, transactions);

  // DAS-3129: a holding alert is one whose ID is in the Portfolio Alerts list.
  const pfAlerts = useSelector(state => state?.portfolio?.data?.pf_alert);
  const isHoldingAlert = Array.isArray(pfAlerts)
    && pfAlerts.some(a => Number(a?.ID) === Number(alert?.ID));

  const [newsStory, setNewsStory] = useState(null);
  useEffect(() => {
    if (!isHoldingAlert || !alert?.ticker) return;
    let cancelled = false;
    api.get('/trendsearch_data', { params: { ticker: alert.ticker } })
      .then(response => {
        if (cancelled) return;
        const rows = response?.data?.success ? response.data.data : null;
        setNewsStory(latestAlertStory(rows));
      })
      .catch(() => {
        if (!cancelled) setNewsStory(null);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHoldingAlert, alert?.ticker]);

  const alignmentScore = alert?.LLM_analysis_alignment_score;
  const alignmentExpl = alert?.LLM_analysis_alignment_explanation || 'No explanation provided.';
  const fpScore = alert?.LLM_false_positive_score;
  const fpExpl = alert?.LLM_false_positive_explanation || 'No explanation provided.';
  const finalConfidence = alert?.pf_final_confidence_score;
  const finalConfidenceExpl = alert?.pf_final_confidence_score_explanation || 'No explanation provided.';
  const opportunityFactor = alert?.opportunity_factor;
  // DAS-2475: clause outperformance score
  const outperformanceScore = alert?.clause_outperformance_score;
  const outpNum = parseFloat(outperformanceScore);

  const alignmentColor =
    alignmentScore >= 4 ? 'success' : alignmentScore >= 2 ? 'warning' : 'error';

  const fpColor =
    fpScore >= 8 ? 'error' : fpScore >= 6 ? 'warning' : fpScore >= 3 ? 'default' : 'success';
  // DAS-2473: Color reflects score strength across 0-1000 range
  const finalConfNum = parseFloat(finalConfidence);
  const finalColor =
    !Number.isFinite(finalConfNum) ? 'default' :
    finalConfNum >= 400 ? 'success' : finalConfNum >= 200 ? 'warning' : 'error';
  const oppColor =
    opportunityFactor >= 0.7 ? 'success' : opportunityFactor >= 0.4 ? 'warning' : 'error';
  // DAS-2475: outperformance color
  const outpColor =
    !Number.isFinite(outpNum) ? 'default' :
    outpNum >= 0.75 ? 'success' : outpNum >= 0.50 ? 'warning' : 'error';

  const seenChecked = alert?.seen === 1 || alert?.seen === true;
  const followedChecked = alert?.followed_up === 1 || alert?.followed_up === true;

  const [alignmentOpen, setAlignmentOpen] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [urgencyOpen, setUrgencyOpen] = useState(false);

  // DAS-2532: overall urgency score (headline signal)
  const urgencyScore = urgencyValue(alert);
  const urgencyBandVal = urgencyBand(alert);
  const urgencyExpl = alert?.urgency_score_explanation || 'No explanation provided.';

  // Check if affirmation content is present to adjust card height
  const hasAffirmationContent =
    (alert?.pf_confirmation_txt && alert.pf_confirmation_txt.trim() !== '') ||
    (alert?.pf_contradiction_txt && alert.pf_contradiction_txt.trim() !== '') ||
    (alert?.pf_weighted_confirmation_percentage !== null &&
     alert?.pf_weighted_confirmation_percentage !== undefined) ||
    (finalConfidence !== null && finalConfidence !== undefined);

  // Check if alert is recent (< 96 hours old) - DAS-1420
  const isRecent = isRecentAlert(alert);

  // DAS-2091: Calculate alert age in days from epoch_initiation (oldest leg)
  const alertAgeDays = alert?.epoch_initiation
    ? Math.floor((Date.now() / 1000 - alert.epoch_initiation) / 86400)
    : null;
  // DAS-2701: a combination shows "<newest leg>d / <oldest leg>d". The staleness colour
  // follows the NEWEST leg (recent reinforcement = not stale), so a freshly-strengthened
  // combo no longer looks old. Single alerts keep the one-age behaviour unchanged.
  const isCombination = (alert?.alert_subtype || '').toLowerCase() === 'combination';
  const newestLegAgeDays = alert?.epoch_initiation_newest_leg
    ? Math.floor((Date.now() / 1000 - alert.epoch_initiation_newest_leg) / 86400)
    : null;
  const showSplitAge = isCombination && newestLegAgeDays !== null;
  const ageLabel = showSplitAge
    ? `${newestLegAgeDays}d / ${alertAgeDays}d`
    : (alertAgeDays !== null ? `${alertAgeDays}d` : null);
  const ageForColor = showSplitAge ? newestLegAgeDays : alertAgeDays;
  const ageTooltip = showSplitAge
    ? `Newest leg fired ${newestLegAgeDays}d ago · oldest leg ${alertAgeDays}d ago`
    : `Initiated ${alertAgeDays} day${alertAgeDays !== 1 ? 's' : ''} ago`;

  return (
    <Card sx={{ 
      width: 300,
      // DAS-2693: use minHeight (not a fixed height) so the card grows to fit its
      // content. With a fixed height + MUI's default overflow:hidden, the affirmation
      // badges (capital-fit, Strength, …) pushed the bottom ACTIONS row (Seen /
      // Followed Up) out of view. minHeight keeps the compact look but never clips it.
      minHeight: hasAffirmationContent ? 260 : 220,
      boxShadow: 2,
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      // DAS-1420: Apply blueish background for recent alerts (< 96 hours old)
      backgroundColor: isRecent ? 'rgba(33, 150, 243, 0.08)' : 'background.paper', // Light blue tint
      border: isRecent ? '1px solid rgba(33, 150, 243, 0.3)' : 'none', // Subtle blue border
      // DAS-2532: urgency accent bar (band colour); overrides the left border only
      borderLeft: urgencyBandVal ? `4px solid ${urgencyAccent(urgencyBandVal)}` : undefined,
      // DAS-2701: SEEN alerts recede so UNSEEN ones stand out when scanning the grid.
      opacity: seenChecked ? 0.55 : 1,
      '&:hover': {
        boxShadow: 4,
        transform: 'translateY(-2px)',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: isRecent ? 'rgba(33, 150, 243, 0.12)' : 'background.paper' // Slightly darker on hover
      }
    }}>
      <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        {/* Header with close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            {/* DAS-2701: unread dot — only on UNSEEN alerts, so unactioned ones jump out */}
            {!seenChecked && (
              <Tooltip title="Unseen">
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'error.main',
                           flexShrink: 0, boxShadow: '0 0 0 2px rgba(211,47,47,0.18)' }} />
              </Tooltip>
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                cursor: onTickerClick ? 'pointer' : 'default',
                color: onTickerClick ? 'primary.main' : 'inherit',
                '&:hover': onTickerClick ? {
                  textDecoration: 'underline',
                  color: 'primary.dark'
                } : {},
                fontSize: '0.9rem',
                lineHeight: 1.2
              }}
              onClick={onTickerClick ? () => onTickerClick(alert?.ticker) : undefined}
            >
              {alert?.company} ({alert?.ticker})
            </Typography>
            {/* DAS-2532: urgency headline pill — click for exact-calc dialog, hover for tooltip */}
            {urgencyBandVal && (
              <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{urgencyExpl}</span>} placement="top">
                <Chip
                  label={urgencyPillLabel(urgencyBandVal)}
                  color={urgencyColor(urgencyBandVal)}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setUrgencyOpen(true); }}
                  sx={{ fontSize: '0.6rem', height: 18, fontWeight: 'bold', '& .MuiChip-label': { px: 0.6 } }}
                />
              </Tooltip>
            )}
          </Stack>
          {onClose && (
            <IconButton 
              onClick={onClose} 
              size="small" 
              sx={{ p: 0, minWidth: 'auto' }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Alert Type and labels. DAS-3317: ONE row — a label that does not fit shrinks to "…"
            (full text on hover) instead of being cut off at the card edge. */}
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75, flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
          <Chip
            label={alert?.alert_type}
            color={isHarvest ? 'error' : 'success'}
            size="small"
            sx={{ fontSize: '0.7rem', height: '20px', flexShrink: 0 }}
          />
          {/* DAS-3289 / DAS-3317: Ticker Alpha as one short label; the sentence is on hover */}
          {alphaChip && (
            <Tooltip title={alphaChip.tooltip}>
              <Chip
                data-testid="ticker-alpha-chip"
                label={alphaChip.label}
                color={alphaChip.tone}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.65rem', height: '20px', minWidth: 0, flexShrink: 0 }}
              />
            </Tooltip>
          )}
          {trapBadge && (
            <Tooltip title={trapBadge.tooltip}>
              <Chip
                data-testid="alert-value-trap-badge"
                label={trapBadge.text}
                color={trapBadge.color}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.65rem', height: '20px', minWidth: 0 }}
              />
            </Tooltip>
          )}
          {reportBadge && (
            <Tooltip title={reportBadge.tooltip}>
              <Chip
                data-testid="alert-earnings-badge"
                label={reportBadge.text}
                color={reportBadge.color}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.65rem', height: '20px', minWidth: 0 }}
              />
            </Tooltip>
          )}
          {recentAction && (
            <Tooltip title={`⚠ ${recentAction} — this alert points the OPPOSITE way to a trade you made within the last 30 days; weigh it consciously.`}>
              <Chip
                data-testid="das2951-recent-action"
                label={`⚠ ${recentAction}`}
                color="info"
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.65rem', height: '20px', minWidth: 0 }}
              />
            </Tooltip>
          )}
          {riskBacked && (
            <Tooltip title={`Backed by rising risk '${riskBacked}': it hits this holding's sector or region — the macro research independently supports this sell.`}>
              <Chip
                data-testid="das2895-risk-badge"
                label={`rising risk: ${riskBacked}`}
                color="warning"
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.65rem', height: '20px', minWidth: 0 }}
              />
            </Tooltip>
          )}
          {/* DAS-2091 / DAS-2701: Alert age — combinations show "<newest>d / <oldest>d" */}
          {ageLabel !== null && (
            <Tooltip title={ageTooltip}>
              <Chip
                label={ageLabel}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.6rem',
                  height: '18px',
                  minWidth: '28px',
                  flexShrink: 0,
                  ml: 'auto !important',
                  color: ageForColor > 30 ? 'error.main' : ageForColor > 14 ? 'warning.main' : 'text.secondary',
                  borderColor: ageForColor > 30 ? 'error.main' : ageForColor > 14 ? 'warning.main' : 'divider',
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* DAS-3317: the subtype on its own full-width line, one line with "…" (was a narrow column) */}
        <Tooltip title={`${alert?.alert_subtype || 'n/a'}${alert?.alert_subtype_identifier ? ` (${alert.alert_subtype_identifier})` : ''}`}>
          <Typography
            data-testid="alert-subtype-line"
            variant="body2"
            noWrap
            sx={{
              cursor: onOpenMarketAlerts ? 'pointer' : 'default',
              color: onOpenMarketAlerts ? 'primary.main' : 'text.secondary',
              fontSize: '0.7rem',
              mb: 1.5,
              '&:hover': onOpenMarketAlerts ? {
                textDecoration: 'underline',
                color: 'primary.dark'
              } : {}
            }}
            onClick={onOpenMarketAlerts ? () => onOpenMarketAlerts(alert?.alert_subtype_identifier) : undefined}
          >
            {alert?.alert_subtype || 'n/a'}
            {alert?.alert_subtype_identifier && ` (${alert.alert_subtype_identifier})`}
          </Typography>
        </Tooltip>

        {/* DAS-3129: latest TrendSearch news story for a holding alert */}
        {/* DAS-3317: one line with "…"; the whole story and its source are on hover */}
        {newsStory && (
          <Tooltip title={`${newsStory.text} — ${newsStory.date}${newsStory.source ? ` · ${newsStory.source}` : ''}`}>
            <Typography
              data-testid="alert-news-story"
              variant="caption"
              component="a"
              noWrap
              href={newsStory.url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.65rem',
                mb: 1,
                cursor: newsStory.url ? 'pointer' : 'default',
                textDecoration: 'none',
                '&:hover': newsStory.url ? { textDecoration: 'underline' } : {}
              }}
            >
              {newsStory.text} — {newsStory.date}{newsStory.source ? ` · ${newsStory.source}` : ''}
            </Typography>
          </Tooltip>
        )}

        {/* LLM Metrics - Only show if there are metrics to display */}
        {(alignmentScore !== null && alignmentScore !== undefined) || (fpScore !== null && fpScore !== undefined) ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1, display: 'block' }}>
              LLM METRICS
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {alignmentScore !== null && alignmentScore !== undefined && (
                <Chip
                  icon={<InfoOutlined sx={{ fontSize: '0.7rem' }} />}
                  label={`Alignment: ${alignmentScore}/10`}
                  color={alignmentColor}
                  onClick={() => setAlignmentOpen(true)}
                  size="small"
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: '0.6rem',
                    height: '18px'
                  }}
                />
              )}
              {fpScore !== null && fpScore !== undefined && (
                <Chip
                  icon={<InfoOutlined sx={{ fontSize: '0.7rem' }} />}
                  label={`FP Risk: ${fpScore}/10`}
                  color={fpColor}
                  onClick={() => setFpOpen(true)}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.6rem',
                    height: '18px'
                  }}
                />
              )}
              {/* DAS-2767: timing label — the advice still shows, the chip warns about WHEN */}
              {alert?.trade_timing_label && (
                <Tooltip title={alert.trade_timing_reason || ''}>
                  <Chip
                    data-testid="trade-timing-chip"
                    label={
                      alert.trade_timing_label === 'strength' ? 'strength sale ✅'
                        : alert.trade_timing_label === 'weakness' ? 'weakness sale ⚠️'
                        : alert.trade_timing_label === 'chasing' ? 'chasing ⚠️'
                        : 'normal entry ✅'
                    }
                    color={['weakness', 'chasing'].includes(alert.trade_timing_label) ? 'warning' : 'success'}
                    size="small"
                    sx={{
                      cursor: 'default',
                      fontSize: '0.6rem',
                      height: '18px'
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        ) : null}

        {/* Affirmation Metrics - Compact indicators */}
        {( (alert?.pf_confirmation_txt && alert.pf_confirmation_txt.trim() !== '') ||
           (alert?.pf_contradiction_txt && alert.pf_contradiction_txt.trim() !== '') ||
           (alert?.pf_weighted_confirmation_percentage !== null && alert?.pf_weighted_confirmation_percentage !== undefined)
          ) ? (
          <Box sx={{ mb: 1, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.6rem' }}>
              AFFIRMATION
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {alert?.pf_confirmation_txt && alert.pf_confirmation_txt.trim() !== '' && (() => {
                const match = alert.pf_confirmation_txt.match(/Confirmation of the alert \((\d+)% of affirmation clauses\):/);
                const percentage = alert.pf_confirmation_percentage || (match ? parseInt(match[1]) : 0);
                return (
                  <Chip
                    label={`✓ ${percentage}%`}
                    color="success"
                    size="small"
                    sx={{ 
                      fontSize: '0.55rem',
                      height: '16px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                );
              })()}
              {alert?.pf_contradiction_txt && alert.pf_contradiction_txt.trim() !== '' && (() => {
                const match = alert.pf_contradiction_txt.match(/Contradictive to the alert \((\d+)% of affirmation clauses\):/);
                const percentage = alert.pf_contradiction_percentage || (match ? parseInt(match[1]) : 0);
                return (
                  <Chip
                    label={`✗ ${percentage}%`}
                    color="error"
                    size="small"
                    sx={{ 
                      fontSize: '0.55rem',
                      height: '16px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                );
              })()}
              {(alert?.pf_weighted_confirmation_percentage !== null && alert?.pf_weighted_confirmation_percentage !== undefined) && (() => {
                const weighted = parseInt(alert.pf_weighted_confirmation_percentage, 10);
                const label = `Confirmation ${isNaN(weighted) ? 0 : weighted}%`;
                const chip = (
                  <Chip
                    label={label}
                    color="success"
                    size="small"
                    sx={{ 
                      fontSize: '0.55rem',
                      height: '16px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                );
                return alert?.pf_weighted_confirmation_percentage_explanation ? (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{alert.pf_weighted_confirmation_percentage_explanation}</span>} placement="top">
                    {chip}
                  </Tooltip>
                ) : chip;
              })()}
              {(finalConfidence !== null && finalConfidence !== undefined) && (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`Range: 0 – 1,000 (base value 1–10 × confirmation 0–100%)\n\n${finalConfidenceExpl}`}</span>} placement="top">
                  <Chip
                    data-testid="das2953-confidence-tier"
                    label={`Weighted Confidence ${finalConfidence}${alert?.pf_confidence_tier ? ` — ${alert.pf_confidence_tier}` : ''}`}
                    color={finalColor}
                    size="small"
                    sx={{
                      fontSize: '0.55rem',
                      height: '16px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        ) : null}

        {/* Opportunity Factor - Always visible when available */}
        {(opportunityFactor !== null && opportunityFactor !== undefined) && (
          <Box sx={{ mb: 1, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`Opportunity: ${opportunityFactor}\n\n${alert?.opportunity_factor_explanation || 'No breakdown available'}\n\nWeights: Data freshness 40%, Price 30%, Age 30%.`}</span>} placement="top">
                <Chip
                  label={`Opportunity ${Math.round(opportunityFactor * 100)}%`}
                  color={oppColor}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.55rem',
                    height: '16px',
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              </Tooltip>
              {/* DAS-2364: capital-fit inline dot. Three states:
                    'fits' → tiny 🟢 dot
                    'does_not_fit' → tiny 🟡 dot
                    'na' → no dot (don't pollute the list with N/A)
                    NULL → no dot (Harvest / Monitor / pre-DAS-2364) */}
              {(alert?.capital_fit_status === 'fits' || alert?.capital_fit_status === 'does_not_fit') && (
                <Tooltip
                  title={`Capital-Fit (DAS-2364): ${alert.capital_fit_reason || alert.capital_fit_status}`}
                  placement="top"
                >
                  <Chip
                    label={alert.capital_fit_status === 'fits' ? '🟢' : '🟡'}
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      height: '16px',
                      backgroundColor: alert.capital_fit_status === 'fits' ? '#f6ffed' : '#fffbe6',
                      border: `1px solid ${alert.capital_fit_status === 'fits' ? '#b7eb8f' : '#ffe58f'}`,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                </Tooltip>
              )}
              {/* DAS-2475: Clause outperformance score */}
              {Number.isFinite(outpNum) && (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`Fundamental Strength: ${Math.round(outpNum * 100)}%\n\nAverage sector percentile of the fundamental trigger clauses (raw value -> percentile per field):\n\n${alert?.clause_outperformance_explanation || 'No breakdown available'}\n\n≥75%: Strong | 50-74%: Moderate | <50%: Weak`}</span>} placement="top">
                  <Chip
                    label={`Strength ${Math.round(outpNum * 100)}%`}
                    color={outpColor}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.55rem',
                      height: '16px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        )}

        {/* Actions - Always visible at bottom */}
        <Box sx={{ 
          mt: 'auto', 
          pt: 0.5, 
          borderTop: '1px solid #e0e0e0',
          flexShrink: 0,
          minHeight: '32px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.65rem' }}>
              ACTIONS:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!seenChecked}
                  onChange={() => onCheckboxChange(alert.ID, alert.seen, alert.followed_up)}
                  size="small"
                  sx={{ 
                    '& .MuiSvgIcon-root': { 
                      fontSize: '0.8rem' 
                    },
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: 'primary.main' }}>
                  Seen
                </Typography>
              }
              sx={{ margin: 0, mr: 0.5 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!followedChecked}
                  onChange={() => onFollowedUpChange(alert.ID, alert.seen, alert.followed_up)}
                  size="small"
                  sx={{ 
                    '& .MuiSvgIcon-root': { 
                      fontSize: '0.8rem' 
                    },
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: 'primary.main' }}>
                  Followed
                </Typography>
              }
              sx={{ margin: 0 }}
            />
          </Box>
        </Box>
      </CardContent>

      {/* Alignment Dialog */}
      <Dialog open={alignmentOpen} onClose={() => setAlignmentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Alignment Explanation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {alignmentExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlignmentOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* False Positive Risk Dialog */}
      <Dialog open={fpOpen} onClose={() => setFpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>False Positive Risk</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {fpExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DAS-2532: urgency exact-calculation dialog */}
      <Dialog open={urgencyOpen} onClose={() => setUrgencyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Urgency {urgencyScore != null ? Math.round(urgencyScore) : ''}{urgencyBandVal ? ` — ${urgencyBandVal}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {urgencyExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrgencyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CompactAlertCard;
